import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Button } from "@/components/ui/button";
import { Flower2, Play, Pause, RotateCcw, Home, Volume2, VolumeX, Music, HelpCircle, X, Lock, Cat } from "lucide-react";

const FIELD = { hw: 14, hd: 10 };
const R = 0.45; // mouse body radius
const MOUSE_COLORS = [0xc9a27e, 0x9db4d6, 0xd6a3b8, 0xa8c9a0, 0xd9c184, 0xb3a3d6, 0xe0a98f];

// Levels verified for straight-line solvability (clearance = wall half + 0.6)
const LEVELS = [
  { mice: [[-6, 6], [6, 6]], walls: [], exitW: 5.0, hard: [0, 7] },
  { mice: [[-8, 6], [0, 7], [8, 6]], walls: [[-9, -2, 6, 0.6]], exitW: 4.6, hard: [-4, 8] },
  { mice: [[-11, 7], [-4, 8], [4, 8], [11, 7]], walls: [[-11, 1, 6, 0.6], [11, 1, 6, 0.6]], exitW: 4.2, hard: [0, 6] },
  { mice: [[-11, 7], [-4, 8], [4, 8], [11, 7], [0, 5]], walls: [[-11, 1, 6, 0.6], [11, 1, 6, 0.6], [-3, -7.5, 1.2, 0.8], [3, -7.5, 1.2, 0.8]], exitW: 3.8, hard: [0, 8] },
  { mice: [[-10, 7], [-6, 8], [0, 6], [6, 8], [10, 7], [-8, 3]], walls: [[-11, 0, 6, 0.6], [11, 0, 6, 0.6], [-3.2, -7.5, 1.2, 0.8], [3.2, -7.5, 1.2, 0.8], [-7, -2, 2, 0.6]], exitW: 3.6, hard: [4, 4] },
  { mice: [[-10, 7], [-6, 8], [-2, 5], [2, 5], [6, 8], [10, 7], [0, 8]], walls: [[-11, 0, 6, 0.6], [11, 0, 6, 0.6], [-3.2, -7, 1.2, 0.8], [3.2, -7, 1.2, 0.8], [-7, -3, 1.2, 0.8], [7, -3, 1.2, 0.8]], exitW: 3.6, hard: [4, 3] },
  { mice: [[-10, 7], [-4, 8], [0, 8.5], [4, 8], [10, 7]], walls: [[-9, -3, 4, 0.6], [9, -3, 4, 0.6]], exitW: 4.0, hard: [7, 4], cats: [{ pts: [[-10, 1], [10, 1]] }] },
  { mice: [[-10, 7], [-5, 8], [0, 6], [5, 8], [10, 7], [0, 8.5]], walls: [[-11, 1, 5, 0.6], [11, 1, 5, 0.6], [-5, -5, 2, 0.6], [5, -5, 2, 0.6]], exitW: 3.8, hard: [-8, 4], cats: [{ pts: [[-9, 3.5], [9, 3.5]] }, { pts: [[9, -1.5], [-9, -1.5]] }] },
];
const DIFFS = {
  easy: { label: "Easy", speed: 6, tail: 5, exitMul: 1.3, fewer: true, extra: false, cat: 2.0 },
  medium: { label: "Medium", speed: 8, tail: 8, exitMul: 1.0, fewer: false, extra: false, cat: 2.8 },
  hard: { label: "Hard", speed: 10, tail: 12, exitMul: 0.85, fewer: false, extra: true, cat: 3.6 },
};
const loadUnlocked = () => {
  try { return { easy: 1, medium: 1, hard: 1, ...JSON.parse(localStorage.getItem("garden_unlocked") || "{}") }; }
  catch { return { easy: 1, medium: 1, hard: 1 }; }
};

// ---------------- audio (all generated, royalty-free) ----------------
const makeAudio = () => {
  let ctx = null;
  const ensure = () => (ctx = ctx || new (window.AudioContext || window.webkitAudioContext)());
  const note = (f0, f1, dur, type = "sine", vol = 0.05, when = 0) => {
    try {
      const c = ensure();
      const t = c.currentTime + when;
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = type;
      o.frequency.setValueAtTime(f0, t);
      o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      o.connect(g).connect(c.destination);
      o.start(t);
      o.stop(t + dur + 0.02);
    } catch { /* no audio */ }
  };
  return {
    ensure,
    select: () => note(700, 900, 0.08, "square", 0.03),
    launch: () => note(240, 90, 0.2, "sawtooth", 0.03),
    escape: () => { note(523, 523, 0.12, "sine", 0.06); note(659, 659, 0.12, "sine", 0.06, 0.1); note(784, 784, 0.2, "sine", 0.06, 0.2); },
    crash: () => note(110, 35, 0.45, "triangle", 0.11),
    flower: () => note(500, 950, 0.14, "sine", 0.05),
    chirp: () => { note(2100, 2900, 0.07, "sine", 0.035); note(2600, 1900, 0.09, "sine", 0.03, 0.09); },
    meow: () => { note(430, 720, 0.16, "sawtooth", 0.05); note(700, 300, 0.3, "sawtooth", 0.045, 0.15); },
    musicNote: (f, w = "square", v = 0.018) => note(f, f, 0.24, w, v),
  };
};
// one distinct tune per level: t=ms per step, w=waveform, v=volume, n=melody (0 = rest)
const TUNES = [
  { t: 280, w: "sine", v: 0.022, n: [523, 659, 784, 659, 523, 659, 880, 784] },
  { t: 240, w: "triangle", v: 0.03, n: [392, 494, 587, 784, 587, 494, 587, 0] },
  { t: 260, w: "square", v: 0.014, n: [349, 440, 523, 698, 659, 523, 440, 392] },
  { t: 300, w: "triangle", v: 0.03, n: [440, 523, 659, 523, 440, 349, 440, 330] },
  { t: 220, w: "sawtooth", v: 0.012, n: [294, 440, 587, 523, 440, 349, 440, 294] },
  { t: 200, w: "square", v: 0.013, n: [330, 392, 494, 659, 494, 392, 330, 247] },
  { t: 320, w: "triangle", v: 0.032, n: [262, 277, 330, 392, 330, 277, 262, 0] },
  { t: 190, w: "sawtooth", v: 0.013, n: [220, 330, 440, 523, 440, 330, 262, 330] },
];
const CELEB_TUNE = { t: 200, w: "sine", v: 0.024, n: [784, 988, 1175, 1568, 1175, 988, 1319, 1568] };

// ---------------- procedural garden models ----------------
const buildMouseModel = (color) => {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.7 });
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.42, 14, 12), mat);
  body.scale.set(0.85, 0.7, 1.15);
  body.position.y = 0.34;
  body.castShadow = true;
  g.add(body);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.26, 12, 10), mat);
  head.position.set(0, 0.42, 0.42);
  head.castShadow = true;
  g.add(head);
  const earMat = new THREE.MeshStandardMaterial({ color: 0xf0b9c4, roughness: 0.8 });
  const ears = [];
  [-0.16, 0.16].forEach((x) => {
    const ear = new THREE.Mesh(new THREE.SphereGeometry(0.13, 8, 8), earMat);
    ear.scale.set(1, 1, 0.4);
    ear.position.set(x, 0.66, 0.34);
    g.add(ear);
    ears.push(ear);
  });
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x241a14 });
  [-0.1, 0.1].forEach((x) => {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 6), eyeMat);
    eye.position.set(x, 0.48, 0.63);
    g.add(eye);
  });
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 6), new THREE.MeshBasicMaterial({ color: 0xd06a7a }));
  nose.position.set(0, 0.4, 0.68);
  g.add(nose);
  const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.05, 0.55, 6), earMat);
  tail.rotation.x = Math.PI / 2.6;
  tail.position.set(0, 0.25, -0.62);
  g.add(tail);
  g.userData = { body, ears, mat };
  return g;
};

const buildBird = () => {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 10), new THREE.MeshStandardMaterial({ color: 0x4aa3e8, roughness: 0.6 }));
  body.scale.set(0.8, 0.7, 1.2);
  g.add(body);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), new THREE.MeshStandardMaterial({ color: 0x5cb4f5, roughness: 0.6 }));
  head.position.set(0, 0.3, 0.55);
  g.add(head);
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.3, 6), new THREE.MeshStandardMaterial({ color: 0xf5a623 }));
  beak.rotation.x = Math.PI / 2;
  beak.position.set(0, 0.28, 0.88);
  g.add(beak);
  const belly = new THREE.Mesh(new THREE.SphereGeometry(0.35, 10, 8), new THREE.MeshStandardMaterial({ color: 0xfff2d9, roughness: 0.7 }));
  belly.scale.set(0.7, 0.6, 0.9);
  belly.position.set(0, -0.12, 0.15);
  g.add(belly);
  const wingMat = new THREE.MeshStandardMaterial({ color: 0x3b8fd4, roughness: 0.6, side: THREE.DoubleSide });
  const wings = [];
  [-1, 1].forEach((s) => {
    const w = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.06, 0.55), wingMat);
    w.position.set(s * 0.65, 0.1, 0);
    g.add(w);
    wings.push({ mesh: w, side: s });
  });
  g.userData = { wings };
  return g;
};

const flowerColors = [0xff6b9d, 0xffd93d, 0xff8c42, 0xb388ff, 0xff5252, 0x64dd17];
const buildFlower = (color) => {
  const f = new THREE.Group();
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.05, 0.5, 5), new THREE.MeshStandardMaterial({ color: 0x3c8c3c }));
  stem.position.y = 0.25;
  f.add(stem);
  const center = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 6), new THREE.MeshStandardMaterial({ color: 0xffe082 }));
  center.position.y = 0.55;
  f.add(center);
  const petalMat = new THREE.MeshStandardMaterial({ color, roughness: 0.7 });
  for (let i = 0; i < 5; i++) {
    const p = new THREE.Mesh(new THREE.SphereGeometry(0.09, 6, 6), petalMat);
    const a = (i / 5) * Math.PI * 2;
    p.scale.set(1.4, 0.5, 1);
    p.position.set(Math.cos(a) * 0.15, 0.55, Math.sin(a) * 0.15);
    f.add(p);
  }
  return f;
};

const buildTree = () => {
  const t = new THREE.Group();
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.35, 1.6, 8), new THREE.MeshStandardMaterial({ color: 0x7a5230, roughness: 0.9 }));
  trunk.position.y = 0.8;
  trunk.castShadow = true;
  t.add(trunk);
  [[0, 2.2, 0, 1.2], [0.7, 1.7, 0.3, 0.8], [-0.6, 1.8, -0.3, 0.75]].forEach(([x, y, z, r]) => {
    const leaf = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 8), new THREE.MeshStandardMaterial({ color: 0x4a9c48, roughness: 0.85 }));
    leaf.position.set(x, y, z);
    leaf.castShadow = true;
    t.add(leaf);
  });
  return t;
};

const buildCatModel = () => {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0xe08840, roughness: 0.75 });
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.52, 14, 12), mat);
  body.scale.set(0.9, 0.75, 1.5);
  body.position.y = 0.5;
  body.castShadow = true;
  g.add(body);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.34, 12, 10), mat);
  head.position.set(0, 0.74, 0.72);
  head.castShadow = true;
  g.add(head);
  [-0.18, 0.18].forEach((x) => {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.28, 6), mat);
    ear.position.set(x, 1.06, 0.66);
    g.add(ear);
  });
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x39c04f, emissive: 0x1b6e2e, roughness: 0.4 });
  [-0.13, 0.13].forEach((x) => {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 8), eyeMat);
    eye.position.set(x, 0.8, 1.02);
    g.add(eye);
  });
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 6), new THREE.MeshStandardMaterial({ color: 0xd66a7c }));
  nose.position.set(0, 0.7, 1.05);
  g.add(nose);
  const chest = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), new THREE.MeshStandardMaterial({ color: 0xf6e7d3, roughness: 0.85 }));
  chest.position.set(0, 0.42, 0.66);
  g.add(chest);
  const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.9, 6), mat);
  tail.position.set(0, 0.8, -0.85);
  tail.rotation.x = 0.9;
  g.add(tail);
  g.userData = { body, tail };
  return g;
};

export default function GardenEscape() {
  const mountRef = useRef(null);
  const S = useRef({}).current;
  const audioRef = useRef(null);
  const [phase, setPhase] = useState("menu"); // menu|playing|paused|gameover|complete|celebration
  const [level, setLevel] = useState(1);
  const [diff, setDiff] = useState("medium");
  const [remaining, setRemaining] = useState(0);
  const [soundOn, setSoundOn] = useState(true);
  const [musicOn, setMusicOn] = useState(true);
  const [howto, setHowto] = useState(false);
  const [hint, setHint] = useState("");
  const [catGotIt, setCatGotIt] = useState(false);
  const [unlocked, setUnlocked] = useState(loadUnlocked);
  const refs = useRef({ phase: "menu", soundOn: true, musicOn: true, diff: "medium", level: 1 });
  useEffect(() => { refs.current.phase = phase; }, [phase]);
  useEffect(() => { refs.current.soundOn = soundOn; }, [soundOn]);
  useEffect(() => { refs.current.musicOn = musicOn; }, [musicOn]);
  useEffect(() => { refs.current.diff = diff; }, [diff]);
  useEffect(() => { refs.current.level = level; }, [level]);
  const sfx = (name) => { if (refs.current.soundOn) audioRef.current?.[name](); };

  // background music: a different tune per level, festive tune on celebration
  useEffect(() => {
    let step = 0, last = 0, lastKey = "";
    const id = setInterval(() => {
      const p = refs.current.phase;
      if (!refs.current.musicOn || !(p === "playing" || p === "celebration" || p === "complete")) return;
      const celeb = p === "celebration";
      const tune = celeb ? CELEB_TUNE : TUNES[(refs.current.level - 1) % TUNES.length];
      const key = celeb ? "c" : String(refs.current.level);
      if (key !== lastKey) { lastKey = key; step = 0; last = 0; }
      const now = Date.now();
      if (now - last < tune.t) return;
      last = now;
      const f = tune.n[step % tune.n.length];
      if (f) {
        audioRef.current?.musicNote(f, tune.w, tune.v);
        if (step % 2 === 0) audioRef.current?.musicNote(f / 2, "triangle", tune.v * 0.6);
      }
      step++;
    }, 60);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || S.renderer) return;
    audioRef.current = makeAudio();

    // ---------- scene shell ----------
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x9fd8f2); // friendly sky
    scene.fog = new THREE.Fog(0x9fd8f2, 60, 120);
    const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 300);
    camera.position.set(0, 26, 15);
    camera.lookAt(0, 0, 0.5);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const sun = new THREE.DirectionalLight(0xfff4d6, 1.4);
    sun.position.set(18, 30, 10);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -22; sun.shadow.camera.right = 22;
    sun.shadow.camera.top = 22; sun.shadow.camera.bottom = -22;
    scene.add(sun);

    // grass
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(90, 70), new THREE.MeshStandardMaterial({ color: 0x6fbb5a, roughness: 0.95 }));
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    const inner = new THREE.Mesh(new THREE.PlaneGeometry(FIELD.hw * 2, FIELD.hd * 2), new THREE.MeshStandardMaterial({ color: 0x7ecb67, roughness: 0.95 }));
    inner.rotation.x = -Math.PI / 2;
    inner.position.y = 0.01;
    inner.receiveShadow = true;
    scene.add(inner);
    // stepping stones
    for (let i = 0; i < 6; i++) {
      const st = new THREE.Mesh(new THREE.CylinderGeometry(0.55 + Math.random() * 0.2, 0.6, 0.06, 10), new THREE.MeshStandardMaterial({ color: 0xb9b3a8, roughness: 0.9 }));
      st.position.set(Math.sin(i * 1.6) * 4, 0.03, 8 - i * 3.4);
      st.receiveShadow = true;
      scene.add(st);
    }
    // trees + fence + border flowers (outside play field)
    [[-18, -12], [18, -12], [-19, 8], [19, 8]].forEach(([x, z]) => { const t = buildTree(); t.position.set(x, 0, z); scene.add(t); });
    const fenceMat = new THREE.MeshStandardMaterial({ color: 0xf5f0e6, roughness: 0.8 });
    for (let x = -16; x <= 16; x += 2) {
      [[x, -13], [x, 13]].forEach(([fx, fz]) => {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 1.1, 0.22), fenceMat);
        post.position.set(fx, 0.55, fz);
        scene.add(post);
      });
    }
    for (let z = -12; z <= 12; z += 2) {
      [[-17, z], [17, z]].forEach(([fx, fz]) => {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.22, 1.1, 0.22), fenceMat);
        post.position.set(fx, 0.55, fz);
        scene.add(post);
      });
    }
    for (let i = 0; i < 14; i++) {
      const f = buildFlower(flowerColors[i % flowerColors.length]);
      f.position.set(-15.8 + (i % 7) * 5.3, 0, i < 7 ? -11.6 : 11.6);
      scene.add(f);
    }

    // aim arrow
    const aimGroup = new THREE.Group();
    const aimShaft = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.05, 1), new THREE.MeshBasicMaterial({ color: 0xffffff }));
    const aimHead = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.6, 8), new THREE.MeshBasicMaterial({ color: 0xffd93d }));
    aimHead.rotation.x = -Math.PI / 2;
    aimGroup.add(aimShaft, aimHead);
    aimGroup.visible = false;
    scene.add(aimGroup);

    // bird + petals + sparkles (celebration)
    const bird = buildBird();
    bird.visible = false;
    scene.add(bird);
    const petals = new THREE.Group();
    for (let i = 0; i < 34; i++) {
      const p = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.22), new THREE.MeshBasicMaterial({ color: flowerColors[i % flowerColors.length], side: THREE.DoubleSide, transparent: true, opacity: 0.92 }));
      p.position.set((Math.random() - 0.5) * 26, 4 + Math.random() * 10, (Math.random() - 0.5) * 18);
      p.userData = { vy: 0.9 + Math.random(), ph: Math.random() * 9 };
      petals.add(p);
    }
    petals.visible = false;
    scene.add(petals);

    Object.assign(S, {
      scene, camera, renderer, aimGroup, aimShaft, aimHead, bird, petals,
      levelGroup: null, mice: [], walls: [], cats: [], gapHalf: 3, effects: [],
      selected: null, aiming: false, aimPt: { x: 0, z: 0 },
      running: false, paused: false, over: false, t: 0, shake: 0, pendingOver: 0, chirpAt: 0,
    });

    const size = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      // narrow/portrait screens: pull the camera back so the whole garden stays visible
      S.camK = Math.max(1, Math.min(2.4, 1.2 / Math.max(0.3, w / h)));
      camera.position.set(0, 26 * S.camK, 15 * S.camK);
      camera.lookAt(0, 0, 0.5);
      camera.updateProjectionMatrix();
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(mount);

    // ---------- level construction ----------
    const hedgeMat = new THREE.MeshStandardMaterial({ color: 0x3f8f3c, roughness: 0.9 });
    const hedgeMat2 = new THREE.MeshStandardMaterial({ color: 0x357c33, roughness: 0.9 });
    const addHedge = (grp, x, z, w, d, mat = hedgeMat) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, 1.25, d), mat);
      m.position.set(x, 0.62, z);
      m.castShadow = true;
      m.receiveShadow = true;
      grp.add(m);
      S.walls.push({ x, z, hw: w / 2, hd: d / 2 });
    };

    S.buildLevel = (lv, diffKey) => {
      if (S.levelGroup) { scene.remove(S.levelGroup); }
      const grp = new THREE.Group();
      S.levelGroup = grp;
      scene.add(grp);
      S.walls = [];
      S.effects = [];
      S.selected = null;
      S.aiming = false;
      aimGroup.visible = false;
      S.over = false;
      S.pendingOver = 0;
      S.running = true;
      S.paused = false;
      bird.visible = false;
      petals.visible = false;

      const D = DIFFS[diffKey];
      const L = LEVELS[lv - 1];
      const gapHalf = (L.exitW * D.exitMul) / 2;
      S.gapHalf = gapHalf;
      // boundary hedges with exit gap in the top wall
      const topW = FIELD.hw - gapHalf;
      addHedge(grp, -(gapHalf + topW / 2), -FIELD.hd, topW, 0.7);
      addHedge(grp, gapHalf + topW / 2, -FIELD.hd, topW, 0.7);
      addHedge(grp, 0, FIELD.hd, FIELD.hw * 2 + 1.4, 0.7);
      addHedge(grp, -FIELD.hw, 0, 0.7, FIELD.hd * 2);
      addHedge(grp, FIELD.hw, 0, 0.7, FIELD.hd * 2);
      L.walls.forEach(([x, z, w, d]) => addHedge(grp, x, z, w, d, hedgeMat2));

      // exit arch + glow
      const postMat = new THREE.MeshStandardMaterial({ color: 0x9c6b3f, roughness: 0.8 });
      [-1, 1].forEach((s) => {
        const post = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 2.2, 8), postMat);
        post.position.set(s * gapHalf, 1.1, -FIELD.hd);
        grp.add(post);
      });
      const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, gapHalf * 2 + 0.4, 8), postMat);
      beam.rotation.z = Math.PI / 2;
      beam.position.set(0, 2.2, -FIELD.hd);
      grp.add(beam);
      const glow = new THREE.Mesh(new THREE.PlaneGeometry(gapHalf * 2 - 0.2, 1.6), new THREE.MeshBasicMaterial({ color: 0x9dffa0, transparent: true, opacity: 0.5 }));
      glow.rotation.x = -Math.PI / 2;
      glow.position.set(0, 0.02, -FIELD.hd + 0.6);
      grp.add(glow);
      S.exitGlow = glow;

      // mice
      let spots = [...L.mice];
      if (D.fewer && spots.length > 2) spots = spots.slice(0, spots.length - 1);
      if (D.extra) spots = [...spots, L.hard];
      S.mice = spots.map(([x, z], i) => {
        const model = buildMouseModel(MOUSE_COLORS[i % MOUSE_COLORS.length]);
        model.position.set(x, 0, z);
        grp.add(model);
        const tailPts = new Float32Array(360);
        const tailGeo = new THREE.BufferGeometry();
        tailGeo.setAttribute("position", new THREE.BufferAttribute(tailPts, 3));
        const tailMesh = new THREE.Points(tailGeo, new THREE.PointsMaterial({ color: MOUSE_COLORS[i % MOUSE_COLORS.length], size: 0.32, sizeAttenuation: true, transparent: true, opacity: 0.85 }));
        tailMesh.frustumCulled = false;
        grp.add(tailMesh);
        return {
          i, model, pos: { x, z }, start: { x, z }, dir: { x: 0, z: -1 },
          moving: false, escaped: false, gone: false, escT: 0,
          tail: [], tailMesh, tailGeo, sinceDrop: 0, speed: D.speed, tailLen: D.tail + lv * 0.5,
        };
      });
      setRemaining(S.mice.length);

      // wandering cats (bonus levels)
      S.cats = (L.cats || []).map((c) => {
        const model = buildCatModel();
        model.position.set(c.pts[0][0], 0, c.pts[0][1]);
        grp.add(model);
        return { model, pts: c.pts, fwd: true, pos: { x: c.pts[0][0], z: c.pts[0][1] }, speed: D.cat };
      });
    };

    // ---------- pointer input (mouse + touch via pointer events) ----------
    const groundPt = (clientX, clientY) => {
      const r = renderer.domElement.getBoundingClientRect();
      const nx = ((clientX - r.left) / r.width) * 2 - 1;
      const ny = -(((clientY - r.top) / r.height) * 2 - 1);
      const ray = new THREE.Raycaster();
      ray.setFromCamera(new THREE.Vector2(nx, ny), camera);
      const t = -ray.ray.origin.y / ray.ray.direction.y;
      const p = ray.ray.origin.clone().addScaledVector(ray.ray.direction, t);
      return { x: p.x, z: p.z };
    };
    const onPointerDown = (e) => {
      if (!S.running || S.paused || S.over) return;
      const p = groundPt(e.clientX, e.clientY);
      let best = null, bd = e.pointerType === "mouse" ? 2.2 : 3.4; // fatter pick radius for touch
      for (const m of S.mice) {
        if (m.moving || m.escaped || m.gone) continue;
        const d = Math.hypot(m.pos.x - p.x, m.pos.z - p.z);
        if (d < bd) { bd = d; best = m; }
      }
      if (best) {
        S.selected = best;
        S.aiming = true;
        S.aimPt = p;
        audioRef.current.ensure();
        sfxLocal("select");
        if (refs.current.level === 1) setHint("Drag to aim — the arrow shows the path. Release to run!");
      }
    };
    const onPointerMove = (e) => {
      if (S.aiming) S.aimPt = groundPt(e.clientX, e.clientY);
    };
    const onPointerUp = () => {
      if (!S.aiming || !S.selected) return;
      const m = S.selected;
      const dx = S.aimPt.x - m.pos.x, dz = S.aimPt.z - m.pos.z;
      const len = Math.hypot(dx, dz);
      S.aiming = false;
      aimGroup.visible = false;
      if (len > 0.8) {
        m.dir = { x: dx / len, z: dz / len };
        m.moving = true;
        m.model.rotation.y = Math.atan2(m.dir.x, m.dir.z);
        sfxLocal("launch");
        if (refs.current.level === 1) setHint("Guide every mouse out through the EXIT arch — don't touch hedges, mice or tails!");
      }
      S.selected = null;
    };
    const sfxLocal = (n) => { if (refs.current.soundOn) audioRef.current?.[n](); };
    const el = renderer.domElement;
    el.style.touchAction = "none";
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    // ---------- effects ----------
    const burst = (x, z, color, n = 14, speed = 5) => {
      const g = new THREE.Group();
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true });
      for (let i = 0; i < n; i++) {
        const s = new THREE.Mesh(new THREE.SphereGeometry(0.09, 5, 5), mat.clone());
        s.position.set(x, 0.5, z);
        const a = Math.random() * Math.PI * 2;
        s.userData = { vx: Math.cos(a) * speed * (0.4 + Math.random()), vy: 2.5 + Math.random() * 3, vz: Math.sin(a) * speed * (0.4 + Math.random()) };
        g.add(s);
      }
      g.userData = { life: 0.8, kind: "burst" };
      S.effects.push(g);
      scene.add(g);
    };
    const flowerBed = (x, z) => {
      const g = new THREE.Group();
      const soil = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.15, 0.1, 14), new THREE.MeshStandardMaterial({ color: 0x8a5a35, roughness: 1 }));
      soil.position.set(x, 0.05, z);
      g.add(soil);
      for (let i = 0; i < 6; i++) {
        const f = buildFlower(flowerColors[Math.floor(Math.random() * flowerColors.length)]);
        const a = (i / 6) * Math.PI * 2;
        f.position.set(x + Math.cos(a) * 0.55, 0.05, z + Math.sin(a) * 0.55);
        g.add(f);
      }
      g.scale.setScalar(0.01);
      g.userData = { life: 999, kind: "bed", grow: 0 };
      S.effects.push(g);
      S.levelGroup.add(g);
    };
    S.flowerBed = flowerBed;

    // ---------- collision / stepping ----------
    const collideAt = (m) => {
      if (m.pos.z < -FIELD.hd + 1.0 && Math.abs(m.pos.x) < S.gapHalf - 0.5) return "escape";
      for (const w of S.walls) {
        const dx = Math.max(Math.abs(m.pos.x - w.x) - w.hw, 0);
        const dz = Math.max(Math.abs(m.pos.z - w.z) - w.hd, 0);
        if (dx * dx + dz * dz < R * R) return "collide";
      }
      for (const c of S.cats || []) {
        const dx = c.pos.x - m.pos.x, dz = c.pos.z - m.pos.z;
        if (dx * dx + dz * dz < 1.2) return "cat";
      }
      for (const o of S.mice) {
        if (o === m || o.escaped || o.gone) continue;
        const dx = o.pos.x - m.pos.x, dz = o.pos.z - m.pos.z;
        if (dx * dx + dz * dz < 0.85) return "collide";
        for (const p of o.tail) {
          const tx = p.x - m.pos.x, tz = p.z - m.pos.z;
          if (tx * tx + tz * tz < 0.36) return "collide";
        }
      }
      return "ok";
    };

    const gameOver = (m, byCat = false) => {
      S.over = true;
      S.running = false;
      S.shake = 0.6;
      setCatGotIt(byCat);
      m.model.userData.mat.emissive = new THREE.Color(0x992211);
      burst(m.pos.x, m.pos.z, 0xff5544, 16, 6);
      sfxLocal(byCat ? "meow" : "crash");
      S.pendingOver = 0.7;
    };

    const escape = (m) => {
      m.moving = false;
      m.escaped = true;
      m.escT = 0;
      burst(m.pos.x, m.pos.z, 0xfff08a, 18, 5);
      flowerBed(m.start.x, m.start.z);
      sfxLocal("escape");
      setTimeout(() => sfxLocal("flower"), 250);
      const left = S.mice.filter((x) => !x.escaped && !x.gone).length;
      setRemaining(left);
      if (refs.current.level === 1) setHint(left ? "Wonderful! A flower bed grew where it started. Free the rest!" : "");
      if (left === 0) {
        S.running = false;
        setTimeout(() => {
          if (!S.scene) return;
          const lv = refs.current.level, dk = refs.current.diff;
          if (lv >= LEVELS.length) {
            startCelebration();
          } else {
            setUnlocked((u) => {
              const next = { ...u, [dk]: Math.max(u[dk], lv + 1) };
              localStorage.setItem("garden_unlocked", JSON.stringify(next));
              return next;
            });
            setPhase("complete");
          }
        }, 900);
      }
    };

    const startCelebration = () => {
      setUnlocked((u) => {
        const next = { ...u, [refs.current.diff]: LEVELS.length };
        localStorage.setItem("garden_unlocked", JSON.stringify(next));
        return next;
      });
      // decorate the whole garden
      for (let i = 0; i < 8; i++) flowerBed(-11 + i * 3.1, 2 + Math.sin(i) * 4);
      bird.visible = true;
      petals.visible = true;
      S.chirpAt = 0;
      setPhase("celebration");
    };

    // ---------- main loop ----------
    let lastFrame = performance.now();
    const animate = () => {
      S.raf = requestAnimationFrame(animate);
      const now = performance.now();
      const dt = Math.min(0.05, (now - lastFrame) / 1000);
      lastFrame = now;
      S.t += dt;
      const ph = refs.current.phase;

      if (S.running && !S.paused) {
        for (const m of S.mice) {
          if (m.gone) continue;
          // idle / run animation
          const u = m.model.userData;
          u.body.position.y = 0.34 + Math.sin(S.t * (m.moving ? 14 : 3) + m.i) * (m.moving ? 0.06 : 0.03);
          u.ears.forEach((ear, k) => { ear.rotation.z = Math.sin(S.t * 4 + m.i + k) * 0.15; });
          if (m.escaped) {
            m.escT += dt;
            m.pos.x += m.dir.x * m.speed * dt;
            m.pos.z += m.dir.z * m.speed * dt;
            m.model.position.set(m.pos.x, m.escT * 1.5, m.pos.z);
            m.model.rotation.y += dt * 8;
            m.model.scale.setScalar(Math.max(0.01, 1 - m.escT * 1.2));
            if (m.tail.length) { m.tail.splice(0, 3); m.redraw = true; }
            if (m.escT > 0.8) { m.gone = true; m.model.visible = false; m.tail = []; m.redraw = true; }
          } else if (m.moving) {
            let dist = m.speed * dt;
            while (dist > 0 && m.moving) {
              const step = Math.min(0.22, dist);
              dist -= step;
              m.pos.x += m.dir.x * step;
              m.pos.z += m.dir.z * step;
              m.sinceDrop += step;
              if (m.sinceDrop > 0.32) {
                m.sinceDrop = 0;
                m.tail.push({ x: m.pos.x, z: m.pos.z });
                let total = m.tail.length * 0.32;
                while (total > m.tailLen && m.tail.length) { m.tail.shift(); total -= 0.32; }
                m.redraw = true;
              }
              const res = collideAt(m);
              if (res === "escape") { escape(m); break; }
              if (res === "collide" || res === "cat") { gameOver(m, res === "cat"); break; }
            }
            m.model.position.set(m.pos.x, 0, m.pos.z);
          }
          if (m.redraw) {
            m.redraw = false;
            const arr = m.tailGeo.attributes.position.array;
            m.tail.forEach((p, k) => { arr[k * 3] = p.x; arr[k * 3 + 1] = 0.22; arr[k * 3 + 2] = p.z; });
            m.tailGeo.setDrawRange(0, m.tail.length);
            m.tailGeo.attributes.position.needsUpdate = true;
          }
        }
        // wandering cats
        for (const c of S.cats || []) {
          const target = c.pts[c.fwd ? 1 : 0];
          const dx = target[0] - c.pos.x, dz = target[1] - c.pos.z;
          const d = Math.hypot(dx, dz);
          if (d < 0.15) c.fwd = !c.fwd;
          else {
            c.pos.x += (dx / d) * c.speed * dt;
            c.pos.z += (dz / d) * c.speed * dt;
            let da = Math.atan2(dx, dz) - c.model.rotation.y;
            da = Math.atan2(Math.sin(da), Math.cos(da));
            c.model.rotation.y += da * Math.min(1, dt * 5);
          }
          c.model.position.set(c.pos.x, 0, c.pos.z);
          const cu = c.model.userData;
          cu.body.position.y = 0.5 + Math.sin(S.t * 8) * 0.04;
          cu.tail.rotation.z = Math.sin(S.t * 3) * 0.35;
        }
        // aiming arrow
        if (S.aiming && S.selected) {
          const m = S.selected;
          const dx = S.aimPt.x - m.pos.x, dz = S.aimPt.z - m.pos.z;
          const len = Math.min(6, Math.hypot(dx, dz));
          if (len > 0.4) {
            aimGroup.visible = true;
            const a = Math.atan2(dx, dz);
            aimGroup.position.set(m.pos.x, 0.3, m.pos.z);
            aimGroup.rotation.y = a;
            S.aimShaft.scale.z = len;
            S.aimShaft.position.z = len / 2;
            S.aimHead.position.z = len + 0.25;
          } else aimGroup.visible = false;
          m.model.rotation.y = Math.atan2(dx, dz);
        }
        if (S.exitGlow) S.exitGlow.material.opacity = 0.35 + Math.sin(S.t * 3) * 0.2;
      }

      // pending game-over overlay
      if (S.pendingOver > 0) {
        S.pendingOver -= dt;
        if (S.pendingOver <= 0) setPhase("gameover");
      }
      // camera shake
      if (S.shake > 0) {
        S.shake = Math.max(0, S.shake - dt * 1.4);
        camera.position.x = (Math.random() - 0.5) * S.shake;
        camera.position.y = 26 * (S.camK || 1) + (Math.random() - 0.5) * S.shake;
      }

      // effects
      for (const g of [...S.effects]) {
        if (g.userData.kind === "burst") {
          g.userData.life -= dt;
          g.children.forEach((p) => {
            p.position.x += p.userData.vx * dt;
            p.position.y += p.userData.vy * dt;
            p.position.z += p.userData.vz * dt;
            p.userData.vy -= 9 * dt;
            p.material.opacity = Math.max(0, g.userData.life / 0.8);
          });
          if (g.userData.life <= 0) { scene.remove(g); S.effects.splice(S.effects.indexOf(g), 1); }
        } else if (g.userData.kind === "bed" && g.userData.grow < 1) {
          g.userData.grow = Math.min(1, g.userData.grow + dt * 2.2);
          const s = 0.01 + g.userData.grow * 0.99;
          g.scale.setScalar(1 + Math.sin(g.userData.grow * Math.PI) * 0.18 * (1 - g.userData.grow) + 0);
          g.scale.multiplyScalar(s);
        }
      }

      // celebration
      if (ph === "celebration") {
        const a = S.t * 0.8;
        bird.position.set(Math.cos(a) * 8, 6 + Math.sin(S.t * 1.7) * 0.8, Math.sin(a) * 6);
        bird.rotation.y = -a + Math.PI;
        bird.userData.wings.forEach((w) => { w.mesh.rotation.z = w.side * Math.sin(S.t * 14) * 0.6; });
        petals.children.forEach((p) => {
          p.position.y -= p.userData.vy * dt;
          p.position.x += Math.sin(S.t * 2 + p.userData.ph) * dt * 0.8;
          p.rotation.x += dt * 2;
          p.rotation.y += dt * 1.4;
          if (p.position.y < 0.2) p.position.y = 10 + Math.random() * 4;
        });
        if (S.t > S.chirpAt) { S.chirpAt = S.t + 1.6 + Math.random() * 2; sfxLocal("chirp"); }
      }

      renderer.render(scene, camera);
    };
    animate();

    window.__GE_TEST = {
      launch: (i, dx, dz) => {
        const m = S.mice?.[i];
        if (!m || m.moving || m.escaped || m.gone || !S.running || S.paused || S.over) return false;
        const len = Math.hypot(dx, dz) || 1;
        m.dir = { x: dx / len, z: dz / len };
        m.moving = true;
        m.model.rotation.y = Math.atan2(m.dir.x, m.dir.z);
        return true;
      },
      state: () => ({
        phase: refs.current.phase,
        level: refs.current.level,
        gapHalf: S.gapHalf,
        mice: (S.mice || []).map((m) => ({ x: +m.pos.x.toFixed(2), z: +m.pos.z.toFixed(2), moving: m.moving, escaped: m.escaped, gone: m.gone })),
        cats: (S.cats || []).map((c) => ({ x: +c.pos.x.toFixed(2), z: +c.pos.z.toFixed(2) })),
      }),
      worldToScreen: (x, z) => {
        const v = new THREE.Vector3(x, 0.3, z).project(camera);
        const r = renderer.domElement.getBoundingClientRect();
        return { x: r.left + ((v.x + 1) / 2) * r.width, y: r.top + ((1 - (v.y + 1) / 2)) * r.height };
      },
    };

    return () => {
      delete window.__GE_TEST;
      cancelAnimationFrame(S.raf);
      ro.disconnect();
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      renderer.dispose();
      mount.removeChild(renderer.domElement);
      Object.keys(S).forEach((k) => delete S[k]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- UI actions ----------
  const startLevel = (lv) => {
    setLevel(lv);
    refs.current.level = lv;
    S.buildLevel?.(lv, refs.current.diff);
    setPhase("playing");
    setHint(lv === 1 ? "Tap or click a mouse to pick it up…" : LEVELS[lv - 1].cats ? "Bonus level — a cat is prowling! Don't let a running mouse cross its path." : "");
    audioRef.current?.ensure();
  };
  const restartLevel = () => startLevel(refs.current.level);
  const toMenu = () => {
    if (S.levelGroup) { S.scene.remove(S.levelGroup); S.levelGroup = null; }
    S.running = false;
    if (S.bird) S.bird.visible = false;
    if (S.petals) S.petals.visible = false;
    setPhase("menu");
    setHint("");
  };
  const togglePause = () => {
    if (phase === "playing") { S.paused = true; setPhase("paused"); }
    else if (phase === "paused") { S.paused = false; setPhase("playing"); }
  };
  const maxUnlocked = unlocked[diff] || 1;

  const overlayCls = "absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 backdrop-blur-[2px] px-4 text-center";
  const diffBtn = (k) => (
    <button key={k} onClick={() => setDiff(k)}
      className={`px-4 py-1.5 rounded-full font-mono text-xs font-bold border transition-colors ${diff === k ? "bg-emerald-400 text-emerald-950 border-emerald-300" : "bg-white/10 text-white border-white/25 hover:bg-white/20"}`}
      data-testid={`ge-diff-${k}`}>
      {DIFFS[k].label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="ge-game">
      <div className="relative w-full rounded-xl overflow-hidden border border-border/60 bg-black select-none" style={{ height: "min(68vh, 660px)", minHeight: 440 }}>
        <div ref={mountRef} className="absolute inset-0" data-testid="ge-canvas" />

        {/* HUD */}
        {(phase === "playing" || phase === "paused") && (
          <div className="absolute top-0 inset-x-0 flex items-start justify-between gap-1 px-2 sm:px-3 py-2" data-testid="ge-hud">
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 font-mono text-[10px] sm:text-xs font-bold">
              <span className="bg-black/50 text-amber-200 px-2 sm:px-2.5 py-1 rounded-full" data-testid="ge-level">Level {level}/{LEVELS.length}</span>
              <span className="bg-black/50 text-emerald-300 px-2 sm:px-2.5 py-1 rounded-full" data-testid="ge-difficulty">{DIFFS[diff].label}</span>
              <span className="bg-black/50 text-pink-200 px-2 sm:px-2.5 py-1 rounded-full" data-testid="ge-remaining">Mice left: {remaining}</span>
              {LEVELS[level - 1]?.cats && (
                <span className="bg-amber-500/80 text-amber-950 px-2 sm:px-2.5 py-1 rounded-full flex items-center gap-1" data-testid="ge-cat-badge"><Cat className="w-3.5 h-3.5" /><span className="hidden sm:inline"> Cat on patrol!</span></span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={restartLevel} className="p-2 sm:p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70" title="Restart level" data-testid="ge-restart-btn"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={togglePause} className="p-2 sm:p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70" title="Pause" data-testid="ge-pause-btn"><Pause className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {phase === "playing" && hint && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 max-w-md bg-black/60 text-amber-100 font-mono text-[11px] px-4 py-2 rounded-full" data-testid="ge-hint">{hint}</div>
        )}

        {/* MENU */}
        {phase === "menu" && (
          <div className={overlayCls} data-testid="ge-menu">
            <p className="font-mono text-3xl sm:text-4xl font-bold text-emerald-300 drop-shadow-[0_0_12px_rgba(60,220,130,0.7)] tracking-wide">GARDEN ESCAPE</p>
            <p className="font-mono text-xs text-zinc-200 max-w-md">Guide every little mouse out of the garden maze. Aim well — hedges, other mice and their tails end the run!</p>
            <div className="flex items-center gap-2">{Object.keys(DIFFS).map(diffBtn)}</div>
            <div className="flex flex-wrap items-center justify-center gap-1.5 max-w-sm">
              {LEVELS.map((L, i) => {
                const lv = i + 1, locked = lv > maxUnlocked;
                return (
                  <button key={lv} disabled={locked} onClick={() => startLevel(lv)}
                    title={L.cats ? "Bonus level — a cat prowls the garden!" : `Level ${lv}`}
                    className={`w-9 h-9 rounded-lg font-mono text-xs font-bold border flex items-center justify-center ${locked ? "bg-white/5 text-zinc-500 border-white/10" : "bg-white/15 text-white border-white/30 hover:bg-emerald-400 hover:text-emerald-950"} ${L.cats && !locked ? "ring-1 ring-amber-400/80" : ""}`}
                    data-testid={`ge-level-btn-${lv}`}>
                    {locked ? <Lock className="w-3.5 h-3.5" /> : L.cats ? <span className="flex flex-col items-center leading-none"><Cat className="w-3 h-3 text-amber-300" />{lv}</span> : lv}
                  </button>
                );
              })}
            </div>
            <p className="font-mono text-[10px] text-amber-300/90 flex items-center gap-1"><Cat className="w-3 h-3" /> Bonus levels 7–8: a cat prowls the garden — time your runs!</p>
            <Button onClick={() => startLevel(Math.min(maxUnlocked, LEVELS.length))} className="btn-3d h-11 px-10 font-mono text-sm font-bold" data-testid="ge-play-btn">
              <Play className="w-4 h-4 mr-2" /> Play
            </Button>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button onClick={() => setHowto(true)} className="flex items-center gap-1 font-mono text-[11px] text-zinc-200 bg-white/10 px-3 py-1.5 rounded-full hover:bg-white/20" data-testid="ge-howto-btn"><HelpCircle className="w-3.5 h-3.5" /> How to Play</button>
              <button onClick={() => setSoundOn((v) => !v)} className="flex items-center gap-1 font-mono text-[11px] text-zinc-200 bg-white/10 px-3 py-1.5 rounded-full hover:bg-white/20" data-testid="ge-sound-btn">
                {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />} Sound {soundOn ? "On" : "Off"}
              </button>
              <button onClick={() => setMusicOn((v) => !v)} className="flex items-center gap-1 font-mono text-[11px] text-zinc-200 bg-white/10 px-3 py-1.5 rounded-full hover:bg-white/20" data-testid="ge-music-btn">
                <Music className="w-3.5 h-3.5" /> Music {musicOn ? "On" : "Off"}
              </button>
            </div>
          </div>
        )}

        {/* HOW TO PLAY */}
        {howto && (
          <div className={overlayCls} style={{ zIndex: 20 }} data-testid="ge-howto-panel">
            <div className="bg-zinc-900/95 border border-emerald-500/40 rounded-xl p-5 max-w-md text-left space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-mono text-sm font-bold text-emerald-300">How to Play</p>
                <button onClick={() => setHowto(false)} data-testid="ge-howto-close"><X className="w-4 h-4 text-zinc-400" /></button>
              </div>
              <ol className="font-mono text-[11px] text-zinc-200 space-y-1.5 list-decimal list-inside">
                <li>Tap or click a mouse to select it.</li>
                <li>Drag to aim — an arrow shows the direction it will run.</li>
                <li>Release to send it running in a straight line.</li>
                <li>It must leave through the glowing EXIT arch at the top.</li>
                <li>Touching a hedge, another mouse, or any tail ends the level.</li>
                <li>Several mice can run at once — mind their tails!</li>
                <li>Each escape grows a flower bed. Free every mouse to win the level.</li>
                <li>Bonus levels 7–8: a cat prowls the garden — a running mouse that crosses its path gets caught!</li>
              </ol>
            </div>
          </div>
        )}

        {/* PAUSE */}
        {phase === "paused" && (
          <div className={overlayCls} data-testid="ge-pause-overlay">
            <p className="font-mono text-2xl font-bold text-white">Paused</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={togglePause} className="btn-3d h-9 font-mono text-xs" data-testid="ge-resume-btn"><Play className="w-3.5 h-3.5 mr-1" /> Resume</Button>
              <Button onClick={restartLevel} variant="outline" className="h-9 font-mono text-xs" data-testid="ge-pause-restart"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restart Level</Button>
              <Button onClick={toMenu} variant="outline" className="h-9 font-mono text-xs" data-testid="ge-pause-menu"><Home className="w-3.5 h-3.5 mr-1" /> Main Menu</Button>
            </div>
          </div>
        )}

        {/* GAME OVER */}
        {phase === "gameover" && (
          <div className={overlayCls} data-testid="ge-gameover">
            <p className="font-mono text-3xl font-bold text-rose-400">Oh no!</p>
            <p className="font-mono text-xs text-zinc-200" data-testid="ge-gameover-msg">{catGotIt ? "The cat caught a mouse! Watch its patrol and time your runs." : "A mouse bumped into something. Try a different order or angle!"}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={restartLevel} className="btn-3d h-10 px-6 font-mono text-xs font-bold" data-testid="ge-go-restart"><RotateCcw className="w-4 h-4 mr-1" /> Restart Level</Button>
              <Button onClick={toMenu} variant="outline" className="h-10 px-6 font-mono text-xs" data-testid="ge-go-menu"><Home className="w-4 h-4 mr-1" /> Main Menu</Button>
            </div>
          </div>
        )}

        {/* LEVEL COMPLETE */}
        {phase === "complete" && (
          <div className={overlayCls} data-testid="ge-levelcomplete">
            <p className="font-mono text-3xl font-bold text-emerald-300">Level {level} Complete!</p>
            <p className="font-mono text-xs text-zinc-200">Every mouse escaped — the garden grows prettier!</p>
            <div className="flex items-center gap-2">
              <Button onClick={() => startLevel(level + 1)} className="btn-3d h-10 px-6 font-mono text-xs font-bold" data-testid="ge-next-btn"><Play className="w-4 h-4 mr-1" /> Next Level</Button>
              <Button onClick={toMenu} variant="outline" className="h-10 px-6 font-mono text-xs" data-testid="ge-complete-menu"><Home className="w-4 h-4 mr-1" /> Main Menu</Button>
            </div>
          </div>
        )}

        {/* CELEBRATION */}
        {phase === "celebration" && (
          <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-start pt-8" data-testid="ge-celebration">
            <p className="font-mono text-3xl sm:text-4xl font-bold text-amber-300 drop-shadow-[0_0_14px_rgba(255,210,80,0.9)] animate-bounce">Garden Complete!</p>
            <p className="font-mono text-xs text-white/90 mt-1">Every mouse is free and the garden is in full bloom!</p>
            <div className="flex items-center gap-2 mt-4 pointer-events-auto">
              <Button onClick={() => { setLevel(1); startLevel(1); }} className="btn-3d h-10 px-6 font-mono text-xs font-bold" data-testid="ge-again-btn"><Play className="w-4 h-4 mr-1" /> Play Again</Button>
              <Button onClick={toMenu} variant="outline" className="h-10 px-6 font-mono text-xs bg-black/40 text-white" data-testid="ge-celebration-menu"><Home className="w-4 h-4 mr-1" /> Main Menu</Button>
            </div>
          </div>
        )}
      </div>
      <p className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
        <Flower2 className="w-3 h-3 text-pink-400" /> Tap a mouse, drag to aim, release to run · works with mouse and touch · progress saves per difficulty on this device
      </p>
    </div>
  );
}
